import { Spinner } from '@flatfile/baseline'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

interface SheetOverlayProps {
  isVisible: boolean
  message?: string
  minimumDuration?: number // In milliseconds
  exitDelay?: number // In milliseconds
}

export function SheetOverlay({
  isVisible,
  message = 'Initializing sheet data...',
  minimumDuration = 1000,
}: SheetOverlayProps) {
  const [shouldShow, setShouldShow] = useState(isVisible)
  const showTimestampRef = useRef<number | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isVisible) {
      setShouldShow(true)
      showTimestampRef.current = Date.now()

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    } else if (showTimestampRef.current) {
      const elapsedTime = Date.now() - showTimestampRef.current

      if (elapsedTime < minimumDuration) {
        const remainingTime = minimumDuration - elapsedTime
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          setShouldShow(false)
          showTimestampRef.current = null
        }, remainingTime)
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          setShouldShow(false)
          showTimestampRef.current = null
        }, minimumDuration)
      }
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isVisible, minimumDuration])

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          className='absolute inset-0 backdrop-blur-sm z-10 flex flex-col items-center justify-center'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
          }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{
              delay: 0.1,
              duration: 0.4,
              ease: 'easeOut',
            }}
          >
            <Spinner />
          </motion.div>

          <motion.p
            className='text-gray-700 font-medium mt-4'
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{
              delay: 0.2,
              duration: 0.4,
              ease: 'easeOut',
            }}
          >
            {message}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
